import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import { genericGet } from "../../services/apiServices";
import { GET_EXPORT_SHIPMENT_COSTING } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { getColorById, getColorPlate, getCustumersList } from "../../services/CommonServices";
import { genericPost } from "../../services/authServices";
import CustomFilter from "../common/CustomFilter";
import CustomAutocomplete from "../common/CustomAutoComplete";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomButton from "../common/CustomButton";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomTextField from "../common/CustomTextField";
import CustomStatus from "../common/CustomStatus";

const title = "Export Shipment Costing Report";

const ExportShipmentCostingReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [custumersList, setCustumersList] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);
    const [filterPayload, setFilterPayload] = useState({
        customerNamesId: '',
        fromDate: '',
        toDate: '',

    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                customerNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);
    const getCommonData = async () => {
        const custumersList = await getCustumersList();
        setCustumersList(custumersList);
        const colorData = await getColorPlate();
        setColorlist(colorData);

    }
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])


    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'etaDate', header: 'Date', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment Id', size: 150, tableStyle: { textAlign: "left" }, },
            { accessorKey: 'piNumber', header: 'PI No.', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'commercialInvoiceNo', header: 'CI No.', size: 180, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'bookingNumber', header: 'Booking No.', size: 180, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'clientCompanyName', header: 'Customer Name', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'numberOfContainers', header: 'No. Of Container', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'totalQty', header: 'Total Qty', size: 100, tableStyle: { textAlign: "center" }, },


            {
                accessorKey: 'totalExpensesInBase', header: 'Total Cost', size: 150, tableStyle: { textAlign: "center" },
            },
            { accessorKey: 'costPerUnit', header: 'Cost per Unit', size: 150, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: "action",
                header: "Action",
                size: 50,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px' }}>
                        <IconButton onClick={() => handleViewClick(row.original.commercialInvExpId)}>
                            <VisibilityIcon />
                        </IconButton>
                    </div>
                ),
            },

        ],
        [permission]
    );
    const handleViewClick = (commercialInvExpId) => {
        navigate(`/app/reports/exportshipmentcostingreport/${commercialInvExpId}`);
    };

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        try {
            setLoading(true);
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            const response = await genericPost(GET_EXPORT_SHIPMENT_COSTING, payload, {});

            if (response.data.success && response.data.commercialInvExps.length > 0) {
                const mappedData = response.data.commercialInvExps.map((invoiceData, index) => {
                    const currencySymbol = invoiceData?.currency?.currency_symbol || '';
                    const baseCurrencySymbol =
                        invoiceData?.exportShipmentList?.company?.baseCurrencyData?.currency_symbol || '';

                    const totalQty = invoiceData?.particulars?.reduce((acc, item) => acc + Number(item.qty || 0), 0) || 0;
                    const proformaInvoiceNo = invoiceData?.ProformaInvExp?.proformaInvoiceNo || "";
                    const proformaAmount = invoiceData?.particulars?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

                    const totalVouchers = invoiceData.receiptVoucher?.length || 0;
                    const totalConversionRate = invoiceData.receiptVoucher?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRatePayment = totalConversionRate > 0 ? (totalConversionRate / totalVouchers).toFixed(2) : "";

                    const totalBooking = invoiceData.shippingBooking?.length || 0;
                    const totalBookingConversionRate = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRateBooking = totalBookingConversionRate > 0 ? (totalBookingConversionRate / totalBooking).toFixed(2) : "1";
                    const halfConversionRate =
                        Number(halfConversionRatePayment) > 0
                            ? Number(halfConversionRatePayment)
                            : Number(halfConversionRateBooking) > 0
                                ? Number(halfConversionRateBooking)
                                : 1;

                    const totalDutyLenght = invoiceData.duty?.length || 0;
                    const boeConversionRate = invoiceData?.duty?.reduce((sum, item) => sum + parseFloat(item.boeConversionRate || 0), 0) || 0;
                    const totalBoeConversionRate = boeConversionRate > 0 ? (boeConversionRate / totalDutyLenght).toFixed(2) : 1;

                    const advanceAmount = invoiceData.advanceReceipt?.reduce((sum, item) => sum + (Number(item.referenceAmount) || 0), 0) || 0;
                    const advanceAmountBaseCurrency = invoiceData.advanceReceipt?.reduce((sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0) || 0;
                    const finalAmount = invoiceData.finalReceipt?.reduce((sum, item) => sum + (Number(item.referenceAmount) || 0), 0) || 0;
                    const finalAmountBaseCurrency = invoiceData.finalReceipt?.reduce((sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0) || 0;
                    const shippingLineCharges = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.shippingLineCharges) || 0), 0) || 0;
                    const bankCharges = invoiceData.receiptVoucher?.reduce((sum, item) => sum + (Number(item.bankCharges) || 0), 0) || 0;

                    const totalDutyAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((dutySum, p) => dutySum + parseFloat(p.duty || 0), 0) || 0);
                    }, 0) || 0;

                    const totalTaxAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((taxSum, p) => taxSum + parseFloat(p.tax || 0), 0) || 0);
                    }, 0) || 0;

                    const totalExpensesInBase = invoiceData?.expenseVoucher?.reduce((sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0) || 0;

                    const currencyTaxSymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTaxList?.currency_symbol || '';
                    const currencyDutySymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDutyList?.currency_symbol || '';

                    let totalTaxAmountInBase = 0;
                    let totalDutyAmountInBase = 0;
                    let taxBaseCurrency = 0;
                    let dutyBaseCurrency = 0;

                    const matchBaseCurrency = parseInt(invoiceData?.exportShipmentList?.company?.baseCurrency || '');
                    const matchTaxCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTax || '');
                    const matchDutyCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDuty || '');

                    if (matchBaseCurrency === matchTaxCurrency) {
                        totalTaxAmountInBase = totalTaxAmount;
                        taxBaseCurrency = currencyTaxSymbol;
                    } else {
                        totalTaxAmountInBase = totalTaxAmount * totalBoeConversionRate;
                        taxBaseCurrency = baseCurrencySymbol;
                    }

                    if (matchBaseCurrency === matchDutyCurrency) {
                        totalDutyAmountInBase = totalDutyAmount;
                        dutyBaseCurrency = currencyDutySymbol;
                    } else {
                        totalDutyAmountInBase = totalDutyAmount * totalBoeConversionRate;
                        dutyBaseCurrency = baseCurrencySymbol;
                    }

                    const totalShipmentExpense = (proformaAmount * halfConversionRate) + bankCharges + totalExpensesInBase + totalDutyAmountInBase || "";

                    const unitName = invoiceData?.particulars?.[0]?.productsUnit?.unitName || '';
                    const productsUnit = invoiceData?.particulars?.[0]?.products?.productsUnit?.unitName || "";

                    const formatAmount = (amt, symbol) => `${symbol} ${Number(amt || 0).toFixed(2)}`;

                    const costPerUnit = totalQty && totalQty !== 0
                        ? ((totalShipmentExpense) / totalQty).toFixed(2)
                        : '0.00';
                    const countryColorObj = getColorById(invoiceData?.customer?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });

                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        commercialInvExpId: invoiceData.commercialInvExpId || '',
                        shipmentId: invoiceData.shipmentId || '',
                        etaDate: companyDateFormat(invoiceData?.date) || '',

                        piNumber: proformaInvoiceNo,
                        clientCompanyName: invoiceData?.customer?.clientCompanyName || "",
                        billingCountry: invoiceData?.customer?.billingCountry?.name || "",
                        numberOfContainers: invoiceData?.shippingBooking?.reduce((sum, item) => sum + Number(item.numberOfContainers || 0), 0) || 0,
                        totalQty: `${totalQty} ${unitName}`,
                        totalExpensesInBase: formatAmount(totalShipmentExpense, baseCurrencySymbol),
                        costPerUnit: `${formatAmount(costPerUnit, baseCurrencySymbol)}${productsUnit ? ' ' + productsUnit : ''}`,

                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                        commercialInvoiceNo: invoiceData?.commercialInvoiceNo || "",

                        bookingNumber: invoiceData?.booking?.bookingNumber || "",

                        numberOfContainers: invoiceData?.booking?.numberOfContainers || 0,
                    };
                });

                setData(mappedData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            }
        } catch (err) {
            setError(err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token, colorlist])

    const defaultView = 'exportshipmentcostingreport';
    const mainContent = (
        <>
            <CustomDataTable
                title={title}
                columns={columns}
                data={data} handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={custumersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Customer"
                                        value={custumersList.find((item) => item.id === filterPayload.customerNamesId) || null}
                                        onChange={(event, newValue) => handleFilterChange("customerNamesId", newValue?.id)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };


    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}

        </>
    );
};
export default ExportShipmentCostingReport;
